import React, { useCallback, useRef, useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../../../hooks/useToast";
import styles from "./AttachmentsSection.module.css";
import { uploadRecordAttachments } from "../../../services/uploadService";
import { sanitizeAttribute } from "../../../../../utils/sanitize";
import AttachmentThumb from "../../../components/shared/AttachmentThumb";
import { generateTempPreview, cleanupBlobUrl } from "../../../utils/mediaProcessing";
import { RECORD_CONSTANTS } from "../../../constants";
import { 
  validateFiles, 
  checkAttachmentLimit, 
  getErrorMessage 
} from "../../../utils/attachmentValidation";

const AttachmentsSection = forwardRef(({ attachments, onUpdate, recordId, sessionId }, ref) => {
  const { t } = useTranslation();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  
  // Track blob URLs for cleanup
  const blobUrlsRef = useRef(new Map());

  // Cleanup blob URLs on unmount
  useEffect(() => {
    // Capture the Map instance at effect creation time
    const urlsMap = blobUrlsRef.current;
    
    return () => {
      // Use the captured urlsMap in cleanup
      urlsMap.forEach((urls) => {
        if (urls.url) cleanupBlobUrl(urls.url);
        if (urls.posterUrl) cleanupBlobUrl(urls.posterUrl);
      });
      urlsMap.clear();
    };
  }, []);

  const handleAddAttachment = useCallback(() => {
    fileInputRef.current?.click();
  }, []);
  
  // Expose method to parent component
  useImperativeHandle(ref, () => ({
    triggerFilePicker: handleAddAttachment
  }), [handleAddAttachment]);

  const handleFileSelect = useCallback(async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setError(null);
    setIsUploading(true);
    setUploadProgress(0);

    // Check if we can upload (need either recordId or sessionId)
    if (!recordId && !sessionId) {
      setError(
        t(
          "records.editor.attachments.errors.missing_target",
          "Cannot upload files without a record ID or session ID."
        )
      );
      setIsUploading(false);
      return;
    }

    try {
      // Validate files
      const { validFiles, errors } = validateFiles(files);
      
      if (errors.length > 0) {
        const errorMessage = errors
          .map(({ fileName, i18nKey, params, message }) => {
            const localized = t(i18nKey, {
              ...(params || {}),
              defaultValue: message
            });
            return `${fileName}: ${localized}`;
          })
          .join(', ');
        setError(errorMessage);
        // Auto-dismiss validation errors after 5 seconds
        toast.error(errorMessage, 5000);
        if (validFiles.length === 0) {
          setIsUploading(false);
          return;
        }
      }

      // Check total attachment count
      const limitCheck = checkAttachmentLimit(attachments.length, validFiles.length);
      if (!limitCheck.allowed) {
        const limitErrorMessage = t(limitCheck.i18nKey, {
          ...(limitCheck.params || {}),
          defaultValue: limitCheck.error
        });
        setError(limitErrorMessage);
        // Auto-dismiss validation errors after 5 seconds
        toast.error(limitErrorMessage, 5000);
        setIsUploading(false);
        return;
      }

      // Upload valid files (to temp if sessionId provided, otherwise permanent)
      const uploadResults = await uploadRecordAttachments(
        validFiles,
        recordId,
        (current, total) => {
          setUploadProgress(Math.round((current / total) * 100));
        },
        sessionId
      );

      // Generate previews for temporary attachments
      const attachmentsWithPreviews = await Promise.all(
        uploadResults.map(async ({ attachment, originalFile }) => {
          if (attachment.isTemp && (attachment.type === 'image' || attachment.type === 'video') && originalFile) {
            try {
              const preview = await generateTempPreview(originalFile, attachment.type);
              
              // Store blob URLs for cleanup
              if (preview.url || preview.posterUrl) {
                blobUrlsRef.current.set(attachment.id, preview);
              }
              
              // Add preview URLs to attachment
              return {
                ...attachment,
                ...(preview.url && { url: preview.url }),
                ...(preview.posterUrl && { posterUrl: preview.posterUrl })
              };
            } catch (error) {
              console.warn('Failed to generate preview:', error);
              return attachment;
            }
          }
          return attachment;
        })
      );

      // Update attachments
      onUpdate([...attachments, ...attachmentsWithPreviews]);
      
      // Show success notification
      const fileCount = uploadResults.length;
      toast.success(
        t("records.editor.attachments.upload_success", {
          count: fileCount,
          defaultValue:
            fileCount === 1
              ? "1 file uploaded successfully"
              : `${fileCount} files uploaded successfully`,
        })
      );
      
      // Reset file input
      event.target.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = getErrorMessage(error, { t });
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [attachments, onUpdate, recordId, sessionId, t, toast]);

  const handleRemoveAttachment = useCallback(
    (attachmentId) => {
      // Clean up blob URLs for this attachment
      const urls = blobUrlsRef.current.get(attachmentId);
      if (urls) {
        if (urls.url) cleanupBlobUrl(urls.url);
        if (urls.posterUrl) cleanupBlobUrl(urls.posterUrl);
        blobUrlsRef.current.delete(attachmentId);
      }
      
      // Update the attachments list
      onUpdate(attachments.filter((att) => att.id !== attachmentId));
      setError(null);
    },
    [attachments, onUpdate]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const event = { target: { files } };
      handleFileSelect(event);
    }
  }, [handleFileSelect]);

  return (
    <div className={styles.section}>
      <label className={styles.label}>
        {t("records.editor.attachments.label", "Attachments")}
      </label>
      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}
      <div 
        className={styles.grid}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,audio/*,.m4a,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.json"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          aria-label={t(
            "records.editor.attachments.select_files_aria",
            "Select files to attach"
          )}
        />
        {attachments.map((attachment) => (
          <div key={attachment.id} className={styles.attachment}>
            <AttachmentThumb 
              attachment={attachment} 
              variant="compact"
              className={styles.preview}
            />
            <button
              className={styles.removeButton}
              onClick={() => handleRemoveAttachment(attachment.id)}
              aria-label={t("records.editor.attachments.remove_attachment_aria", {
                name:
                  sanitizeAttribute(attachment.name || attachment.metadata?.filename) ||
                  t("records.editor.attachments.attachment_fallback", "attachment"),
                defaultValue: `Remove ${
                  sanitizeAttribute(attachment.name || attachment.metadata?.filename) ||
                  t("records.editor.attachments.attachment_fallback", "attachment")
                }`,
              })}
              type="button"
            >
              ×
            </button>
          </div>
        ))}

        <button
          className={styles.addButton}
          onClick={handleAddAttachment}
          aria-label={t("records.editor.attachments.add_attachment_aria", "Add attachment")}
          type="button"
          disabled={isUploading}
        >
          {isUploading ? (
            <div className={styles.uploadProgress}>
              <span className={styles.progressText}>{uploadProgress}%</span>
            </div>
          ) : (
            <svg viewBox="0 0 24 24" width="24" height="24">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          )}
        </button>
      </div>

      {attachments.length === 0 && !isUploading && (
        <p className={styles.hint}>
          {t("records.editor.attachments.hint", {
            maxSizeMB: RECORD_CONSTANTS.MAX_ATTACHMENT_SIZE / 1024 / 1024,
            defaultValue: `Click + or drag files here to attach (max ${RECORD_CONSTANTS.MAX_ATTACHMENT_SIZE / 1024 / 1024}MB per file)`,
          })}
        </p>
      )}
    </div>
  );
});

AttachmentsSection.displayName = 'AttachmentsSection';

export default AttachmentsSection;
